"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Stethoscope,
  Sparkles,
  Crown,
  Target,
  ShieldCheck,
  Brain,
  ClipboardCheck,
  ListChecks,
  Search,
  Scale,
  Activity,
  ArrowRight,
  Mic,
  Award,
  BookOpen,
  CheckCircle2,
  Calendar,
  GraduationCap,
  Building2,
  Briefcase,
  GitBranch,
  Heart,
  Layers,
  Eye,
  Users,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ArchitectureDiagramBlock } from "@/lib/blocks/architecture-diagram-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const EXCELLENCE_CENTER = {
  label: "Excellence Operating System",
  body: "The integrated architecture that converts declared intent into measured outcomes across sovereign-scale operations.",
  icon: <Crown className="size-5" />,
  accent: "amber-gold" as const,
};

const EXCELLENCE_SATELLITES = [
  { label: "Protocols", body: "Standardize the repeatable to eliminate variability in every recurring process.", icon: <ClipboardCheck className="size-4" />, accent: "red" as const },
  { label: "Checklists", body: "Catch the errors that expertise, experience, and intelligence overlook under pressure.", icon: <ListChecks className="size-4" />, accent: "purple" as const },
  { label: "Failure Reviews", body: "Transform individual mistakes into institutional learning through blame-free examination.", icon: <Search className="size-4" />, accent: "cyan" as const },
  { label: "Outcome Accountability", body: "Measure results rather than effort and hold every process accountable for deliverables.", icon: <Scale className="size-4" />, accent: "emerald" as const },
  { label: "Diagnostic Discipline", body: "Form a complete picture before acting. Diagnosis precedes every intervention.", icon: <Stethoscope className="size-4" />, accent: "amber-gold" as const },
  { label: "Sovereign Verification", body: "Test every claim under conditions where the margin for error is zero and the stakes are national.", icon: <ShieldCheck className="size-4" />, accent: "denim" as const },
  { label: "Closed-Loop Learning", body: "Feed every deviation back into the protocols, checklists, and accountability structures.", icon: <GitBranch className="size-4" />, accent: "coral" as const },
  { label: "Durability Architecture", body: "Build the system to outlast the individual. Outcomes compound over decades.", icon: <Award className="size-4" />, accent: "lime" as const },
];

const EXCELLENCE_CONNECTIONS = [
  { from: 0, to: 1, label: "Verifies" },
  { from: 1, to: 2, label: "Surfaces" },
  { from: 2, to: 3, label: "Informs" },
  { from: 3, to: 4, label: "Drives" },
  { from: 4, to: 5, label: "Validates" },
  { from: 5, to: 6, label: "Enables" },
  { from: 6, to: 7, label: "Compounds" },
  { from: 7, to: 0, label: "Reinforces" },
];

const PAST_STAGES = [
  { title: "The Nobel Peace Prize", body: "Among the most prestigious gatherings in the world. Operating authority on the global stage.", icon: <Award className="size-4" />, accent: "amber-gold" as const },
  { title: "World Economic Forum", body: "Where global leaders convene to address the most consequential challenges of the age.", icon: <GlobeIcon className="size-4" />, accent: "cyan" as const },
  { title: "Sovereign Engagements", body: "Government and sovereign-level forums where the stakes are national and the consequences irreversible.", icon: <Building2 className="size-4" />, accent: "red" as const },
  { title: "Corporate Leadership", body: "Fortune-scale organizations seeking operational authority and the architectural foundation for excellence.", icon: <Briefcase className="size-4" />, accent: "purple" as const },
  { title: "Academic Forums", body: "University and academic programs sharing the operating method with the next generation of leaders.", icon: <GraduationCap className="size-4" />, accent: "emerald" as const },
  { title: "Defense Conferences", body: "The intersection of technology, governance, and national security where excellence is non-negotiable.", icon: <ShieldCheck className="size-4" />, accent: "denim" as const },
];

const FAQ_ITEMS = [
  { q: "What is \"How Excellence Actually Runs\" about?", a: "A signature keynote on the operational infrastructure that makes excellence repeatable and accountable. Four components — protocols, checklists, reviews, outcome accountability — drawn from fifteen-plus years of sovereign-scale operations across eighteen countries. The central argument is that excellence is a system to be built, not a value to be believed in.", category: "Overview" },
  { q: "How does this keynote differ from other keynotes on excellence?", a: "The evidence base is sovereign-grade — drawn from operations serving governments, royal families, and defense agencies where the margin for error is zero. The clinical parallel provides rigor that no consulting framework can replicate. The framework is immediately implementable, leaving attendees with a complete operating system rather than a motivational concept.", category: "Differentiation" },
  { q: "What are the four components of the excellence operating system?", a: "Protocols for the repeatable standardize processes that recur to eliminate variability. Checklists for the critical catch the errors that experience and expertise overlook. Reviews for the failures transform individual mistakes into institutional learning through blame-free examination. Outcome accountability measures results rather than effort and holds processes accountable for deliverables.", category: "Framework" },
  { q: "How long is the keynote and what formats are available?", a: "The standard keynote runs forty-five to sixty minutes. Fireside chat runs thirty to forty-five minutes. Workshop and masterclass runs two to three hours. Executive briefing runs sixty to ninety minutes. Panel contribution runs twenty to thirty minutes. All formats are available virtually and in person.", category: "Logistics" },
  { q: "Can the keynote be customized for a specific industry?", a: "Yes. Every keynote is customized to the audience's domain, their specific challenges, and the outcomes they seek. Industries that benefit include healthcare, manufacturing, technology, government, financial services, and defense. The operating method is universal; the application is industry-specific.", category: "Customization" },
  { q: "What outcomes has this operating system produced?", a: "One-hundred-percent client satisfaction. A ninety-nine-point-nine-nine-nine-nine percent operational uptime with a maximum downtime of thirty-one-point-five seconds per year. Zero security incidents across fifteen-plus years. Fifteen out of eighteen political campaigns won. Over nine hundred million voters reached. A ninety-five-percent coordination success rate across nine proprietary platforms.", category: "Evidence" },
  { q: "What makes the clinical parallel credible?", a: "Dr. Jyoti Kush is a physician by training — MBBS in Gynaecology from Delhi — who has spent over a decade applying clinical discipline to sovereign-scale operations. The clinical parallel is not metaphor. It is the operating method that has produced one-hundred-percent client satisfaction and zero security incidents across fifteen-plus years.", category: "Approach" },
  { q: "How far in advance should this keynote be booked?", a: "Early booking is recommended for major conferences, flagship corporate events, and international forums. The customization process typically requires four to six weeks for optimal preparation, though shorter timelines can be accommodated for urgent requirements.", category: "Booking" },
];

const BOOKING_STEPS = [
  { num: "01", title: "Inquiry", body: "The organization submits a speaking inquiry through the contact page with event details, audience profile, topic preference, and desired outcomes. The inquiry provides the diagnostic information that scopes the engagement." },
  { num: "02", title: "Consultation", body: "A preliminary conversation to understand the event objectives, the audience's organizational context, the challenges they face, and the outcomes they seek. The diagnostic phase — understanding the situation before prescribing the keynote." },
  { num: "03", title: "Customization", body: "The keynote is tailored to the audience's domain, their specific challenges, and the outcomes they seek. The operating method is universal; the application is precise. Research into the audience's industry shapes the calibration." },
  { num: "04", title: "Delivery", body: "The keynote is delivered with the same operational discipline that governs every engagement: preparation, precision, and accountability for the outcome. Sovereign-grade delivery, regardless of venue or format." },
  { num: "05", title: "Follow-Up", body: "Post-event resources, frameworks, and follow-up sessions are available for organizations seeking to implement the excellence operating system within their teams. The engagement does not end with the keynote — it begins there." },
];

function GlobeIcon({ className }: { className?: string }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>;
}

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const FOUR_COMPONENTS = [
  {
    num: "01",
    icon: ClipboardCheck,
    title: "Protocols for the Repeatable",
    body: "Standardize the processes that recur. Eliminate the variability that inconsistency breeds. In sovereign-grade operations, protocol adherence produces ninety-five-percent coordination success — a standard that ad hoc execution cannot match.",
    accent: "red",
  },
  {
    num: "02",
    icon: ListChecks,
    title: "Checklists for the Critical",
    body: "Catch the errors that experience, expertise, and intelligence overlook. Surgical checklists reduce complications by over thirty-six percent. The mechanical reliability of a documented process outperforms even the most experienced professional under cognitive load.",
    accent: "purple",
  },
  {
    num: "03",
    icon: Search,
    title: "Reviews for the Failures",
    body: "The meeting most leaders lack the courage to hold. Transform individual mistakes into institutional learning through the routine, peer-reviewed, blame-free examination of every deviation — modeled on the clinical morbidity and mortality conference.",
    accent: "cyan",
  },
  {
    num: "04",
    icon: Scale,
    title: "Accountability for the Outcomes",
    body: "Organizations, like patients, do not care how hard the doctor tried. Outcome accountability measures results, not effort — and holds processes, systems, and individuals accountable for the deliverables they produce.",
    accent: "emerald",
  },
];

const EVIDENCE = [
  { metric: "99.9999", suffix: "%", label: "Operational Uptime", sub: "31.5 seconds max downtime per year", accent: "red" },
  { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "Across 300+ elite clients", accent: "purple" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years sovereign-grade", accent: "cyan" },
  { metric: "15/18", suffix: "", label: "Campaign Victories", sub: "Eighty-three point three percent success", accent: "emerald" },
];

const TAKEAWAYS = [
  { title: "Excellence is a design problem, not a cultural initiative.", body: "Invest in architecture — protocols, checklists, reviews, accountability — not in values, messaging, and alignment exercises. The first step toward operational excellence is recognizing the problem is architectural.", accent: "red" },
  { title: "If the operating steps cannot be written down, the outcome is luck.", body: "Any process that exists only in someone&apos;s head produces inconsistent results. Identify the three most critical processes and document them as protocols — the first action toward operational excellence.", accent: "purple" },
  { title: "Checklists are not for the incompetent — they are for the expert.", body: "Experienced professionals make errors of omission under pressure. Identify the three highest-stakes handoffs and implement checklists that verify completion at each transition point.", accent: "cyan" },
  { title: "The failure review prevents institutional repetition.", body: "Organizations that do not review every deviation repeat the same failures. Implement a routine, blame-free review of deviations with the institutional authority to mandate changes.", accent: "emerald" },
  { title: "Measure outcomes, not effort.", body: "The only metric that matters is the outcome. Replace activity metrics with outcome metrics for the organization&apos;s three most critical deliverables — and hold every process accountable for results.", accent: "amber-gold" },
];

const AUDIENCE = [
  { icon: Crown, title: "C-Suite & Operations Leaders", body: "Decision-makers who recognize the organization has an execution gap and need the architectural solution rather than another motivational stimulus.", accent: "red" },
  { icon: Activity, title: "Quality & Process Leaders", body: "Professionals responsible for operational efficiency, quality management, and continuous improvement — the four-component framework applies directly.", accent: "purple" },
  { icon: Building2, title: "Government Operations", body: "Decision-makers operating at the level where the stakes are national and the consequences of failure are irreversible. Sovereign-scale credibility.", accent: "cyan" },
  { icon: Stethoscope, title: "Healthcare Administration", body: "Healthcare organizations seeking to systematize excellence — the clinical parallel resonates directly with the operational architecture.", accent: "emerald" },
  { icon: Calendar, title: "Conference Organizers", body: "Events seeking a keynote that delivers substance over spectacle. The combination of operational evidence and clinical parallel differentiates the conference.", accent: "amber-gold" },
  { icon: Building2, title: "Quality-Critical Industries", body: "Manufacturing, technology, and quality-critical industries where defects carry disproportionate consequences and process variability is the enemy of excellence.", accent: "denim" },
];

const FORMATS = [
  { label: "Standard Keynote", duration: "45–60 min", body: "Structured presentation from core argument through clinical parallel to the four-component operating system and actionable takeaways.", accent: "red" },
  { label: "Fireside Chat", duration: "30–45 min", body: "Conversational exploration guided by a moderator. Effective for executive audiences who prefer dialogue over monologue.", accent: "purple" },
  { label: "Workshop / Masterclass", duration: "2–3 hrs", body: "Hands-on application of the four components. Tangible output: draft protocols, checklists, review mechanisms, and outcome metrics.", accent: "cyan" },
  { label: "Executive Briefing", duration: "60–90 min", body: "Private, invitation-only diagnostic assessment. The four-component framework applied as a tool to evaluate the organization&apos;s excellence architecture.", accent: "emerald" },
  { label: "Panel Contribution", duration: "20–30 min", body: "Focused contribution to multi-speaker discussions on operational excellence, quality management, or organizational performance.", accent: "amber-gold" },
  { label: "Virtual Delivery", duration: "All formats", body: "All formats available for virtual events with the same operational authority and engagement quality.", accent: "denim" },
];

const GAINS = [
  "The four components of an operating system for excellence — protocols, checklists, reviews, outcome accountability",
  "Why values statements fail where systems succeed — the clinical parallel that makes the argument irrefutable",
  "The architecture of operational excellence as it functions in sovereign-grade environments",
  "Why most organizational excellence initiatives fail — they treat excellence as a cultural initiative, not an engineering problem",
  "The three design principles of effective protocols: specificity, context accommodation, and revision mechanisms",
  "How to implement the failure review without creating blame and with the institutional authority to mandate change",
];

export default function HowExcellenceActuallyRunsPage() {
  return (
    <>
      <PageHero
        icon={Stethoscope}
        overline="Signature Keynote"
        overlineClassName="text-red"
        title={
          <>
            Excellence Is Not a Value. <span className="text-gradient-premium">It Is a System.</span>
          </>
        }
        subtitle="How Excellence Actually Runs is Dr. Jyoti Kush&apos;s signature keynote on the operational infrastructure that makes excellence repeatable, accountable, and immune to the variability of individual performance. The audience leaves with machinery — protocols, checklists, reviews, and accountability structures — not motivation. Verified through sovereign-scale operations across eighteen countries, over three hundred elite clients, and zero security incidents across fifteen-plus years."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Speaking", href: "/speaking/" }, { label: "How Excellence Actually Runs" }]}
        badge="The thesis keynote"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Abstract</span>
          </div>
          <h2 className="text-display-md mb-5">Most organizations believe in excellence. Very few achieve it.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The difference is not ambition. It is architecture. Excellence is not something an organization believes in. It is something an organization has built. When excellence is a value, it is aspirational — subject to interpretation, inconsistent in application. When excellence is a system, it is operational — repeatable regardless of who is in the room.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The keynote dismantles the assumption that excellence is a cultural initiative and demonstrates, with clinical precision, that excellence is an engineering problem — solvable through the same method that produces outcomes in medicine, sovereign-scale operations, and the world&apos;s most demanding environments.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">The Core Argument</span>
          </div>
          <h2 className="text-display-md mb-4">Excellence is a system, not a value.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The evidence drawn from sovereign-scale operations where the margin for error is zero and the consequences of failure are national. The four components of the operating system — demonstrated through real operational examples.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {EVIDENCE.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Four Components</span>
          </div>
          <h2 className="text-display-md mb-4">The operating system for excellence.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each component is presented with the specificity required for immediate implementation: what it is, why it matters, how it works, and what happens in its absence.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {FOUR_COMPONENTS.map((c, i) => {
            const a = ACCENT[c.accent];
            return (
              <motion.div
                key={c.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`size-14 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} shrink-0`}>
                      <c.icon className="size-7 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-caption font-bold ${a.text} opacity-60 mb-1`}>Component {c.num}</div>
                      <h3 className={`text-heading-md ${a.text}`}>{c.title}</h3>
                    </div>
                  </div>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Stethoscope className="size-3.5 text-purple" />
              <span className="text-overline text-purple">The Clinical Parallel</span>
            </div>
            <h2 className="text-display-md mb-4">How medicine systematizes excellence.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Medicine does not achieve excellence by hoping for it. Medicine has built, over centuries, the most rigorous system for producing consistent high-quality outcomes that exists in any human endeavor. The operating method that produces a one-hundred-percent client satisfaction rate across eighteen countries is the same method that produces outcomes in clinical settings.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The clinical skills that keep patients alive are the same skills that keep organizations functioning: the discipline of diagnosing before treating, the skill of triage under pressure, the rigor of following protocols while exercising judgment, the accountability of owning outcomes, and the restraint of first doing no harm.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { title: "Diagnosis Before Treatment", body: "A physician who treats before diagnosing kills patients. A leader who acts before understanding destroys value. The diagnostic discipline prevents both failures.", accent: "red" },
              { title: "Protocol Adherence", body: "Surgical checklists reduce complications by over thirty-six percent. The mechanical reliability of a documented process outperforms even the most experienced professional under cognitive load.", accent: "purple" },
              { title: "Failure Reviews", body: "The morbidity and mortality conference — the routine, peer-reviewed examination of every adverse outcome — is the mechanism that makes medicine the most learning-intensive profession on earth.", accent: "cyan" },
              { title: "Outcome Accountability", body: "The patient lived or did not. Medicine does not credit effort. The same discipline applied to organizations transforms culture from activity measurement to outcome measurement.", accent: "emerald" },
              { title: "Do No Harm Assessment", body: "Every intervention assessed for potential damage before implementation. The most underrated operational discipline — preventing cascading consequences from well-intentioned action.", accent: "amber-gold" },
            ].map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <CheckCircle2 className="size-4 text-white" />
                    </div>
                    <div>
                      <h3 className={`text-heading-sm mb-1 ${a.text}`}>{p.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Key Takeaways</span>
          </div>
          <h2 className="text-display-md mb-4">Five actionable insights for tomorrow.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The keynote is designed to produce immediate operational change — specific, implementable actions that transform the organization&apos;s operating method starting the next business day.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {TAKEAWAYS.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-sm mb-1 ${a.text}`} dangerouslySetInnerHTML={{ __html: t.title }} />
                    <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: t.body }} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-4`}>
            <Users className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Who This Keynote Serves</span>
          </div>
          <h2 className="text-display-md mb-4">Ideal audience for this keynote.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The keynote resonates most strongly with decision-makers who recognize the gap between declaring excellence and delivering it — and who need the architectural solution rather than another motivational stimulus.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCE.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${ac.text}`}>{a.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Briefcase className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Format Options</span>
          </div>
          <h2 className="text-display-md mb-4">Flexibility for every event configuration.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FORMATS.map((f, i) => {
            const a = ACCENT[f.accent];
            return (
              <motion.div
                key={f.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className={`text-heading-sm ${a.text}`}>{f.label}</h3>
                  <span className={`text-caption font-bold ${a.text}`}>{f.duration}</span>
                </div>
                <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Award className="size-3.5 text-lime" />
            <span className="text-overline text-lime">What Attendees Gain</span>
          </div>
          <h2 className="text-display-md mb-4">The value of attending.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six specific takeaways designed to transform organizational capability. Not inspiration — implementation frameworks.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {GAINS.map((g, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={g}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <CheckCircle2 className="size-4 text-white" />
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed flex-1">{g}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book this keynote.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              How Excellence Actually Runs is the foundational keynote in the speaking portfolio. The system that every other topic operates within. Book the keynote that produces measurable operational change — not motivational elevation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book the Keynote
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/speaking/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                All Speaking Topics
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
            <ShieldCheck className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">The Excellence Operating System.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The system is not a checklist of best practices. It is an integrated architecture — a center of verified sovereignty surrounded by eight satellite components that compound into repeatable outcomes. Each satellite enforces a different discipline; together they form the operating system that produces excellence under conditions where the margin for error is zero.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The connections between satellites are causal, not decorative. Protocols enable checklists. Checklists surface deviations. Deviations drive reviews. Reviews inform outcome accountability. Outcome accountability requires diagnostic discipline. Diagnostic discipline is validated by sovereign verification. Verification enables closed-loop learning. Learning compounds into durability. Durability reinforces the protocols. The system closes on itself — and the loop tightens with every cycle.
          </p>
        </div>
        <div className="mt-12">
          <ArchitectureDiagramBlock
            center={EXCELLENCE_CENTER}
            satellites={EXCELLENCE_SATELLITES}
            connections={EXCELLENCE_CONNECTIONS}
          />
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">The Problem This Keynote Solves</span>
          </div>
          <h2 className="text-display-md mb-4">The gap between declaring excellence and delivering it.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The failure is endemic across organizations of every size, industry, and geography. Organizations with "excellence" in their mission statements deliver inconsistent results. Organizations with "quality" as a core value produce variable outputs. Organizations with "best-in-class" aspirations settle for adequate performance. The pattern is universal, and the cause is architectural.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The root cause is the confusion of values with systems. Excellence as a value is a declaration of intent. Aspiration without architecture produces nothing. The organizations that achieve excellence consistently have built the operating infrastructure that produces it. The organizations that achieve it inconsistently have not. Culture without structure is decoration. Structure without culture is machinery. Excellence requires both — and the structure must come first.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The keynote solves this by presenting the architectural solution. Not instead of culture — as the foundation that culture requires. When the operating system demands excellence through protocols, checklists, reviews, and outcome accountability, the culture follows. The operating architecture determines the cultural reality. Always. The evidence base eliminates the skepticism that executive audiences typically bring to organizational improvement initiatives: the four components are drawn from sovereign-scale operations that have produced measurable outcomes across eighteen countries, nine proprietary platforms, and over three hundred elite clients.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-4`}>
            <Award className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Past Stages</span>
          </div>
          <h2 className="text-display-md mb-4">Where this keynote has resonated.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The caliber of the stage reflects the caliber of the work. The keynote is delivered where the stakes are real and the audience demands substance.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PAST_STAGES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    {p.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Calendar className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Booking Process</span>
          </div>
          <h2 className="text-display-md mb-4">Engaging this keynote.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The engagement process follows the same diagnostic method that underpins the keynote itself — five steps from inquiry through follow-up.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
          {BOOKING_STEPS.map((s, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-caption font-bold ${a.text} opacity-60 mb-2`}>Step {s.num}</div>
                <h3 className={`text-heading-sm mb-2 ${a.text}`}>{s.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <AccordionBlock
          items={FAQ_ITEMS}
          eyebrow="Frequently Asked Questions"
          title="What decision-makers ask before booking."
          description="The questions that come up most often in preliminary conversations. Each answer reflects the operating reality, not the marketing narrative."
        />
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Leadership as Diagnosis", description: "The clinical companion to this keynote — the six skills that keep organizations functioning under pressure.", href: "/speaking/leadership-as-diagnosis/", icon: Brain, accent: "purple" },
          { title: "The 70% Project", description: "The talent strategy that populates the excellence operating system with people who execute it.", href: "/speaking/the-70-percent-project/", icon: Heart, accent: "emerald" },
          { title: "Build in the Dark", description: "The cultural disposition that sustains the excellence system over time — built quietly, measured rigorously.", href: "/speaking/build-in-the-dark/", icon: Eye, accent: "denim" },
          { title: "The Triple Threat", description: "The cross-domain integration that excellence requires at the highest levels of consequence.", href: "/speaking/the-triple-threat/", icon: Layers, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
